import React, { useMemo, useState } from 'react';
import { Icon } from '../../../../../components/Icon/Icon';
import { SeatsBlockedActivity, SeatsUnBlockedActivity } from '../../../../../model/activity';
import { cacheRequest } from '../../../../../model/cache';
import { useAsyncValue } from '../../../../../model/data';
import { getEventWithFlatSeats } from '../../../../../model/event';
import { useErrorHandler, getSequences } from '../../../../../util/util';
import { FlatVenuePlanSection, GrouppedBlockedSection } from '../../../../../model/venue';
import './SeatsBlockedActivity.css';
import classnames from 'classnames';
import { groupBy, compact } from 'lodash';
import { KeyValueTable } from '../../../../../components/KeyValueTable/KeyValueTable';
import { t } from '../../../../../locales/translate';
import { Collapse } from '@nerdgeschoss/shimmer-component-collapse';
interface Props {
  activity: SeatsBlockedActivity | SeatsUnBlockedActivity;
}

export const SeatsBlockedActivityBody = ({ activity }: Props): JSX.Element => {
  const [mappedSeats, loading, error] = useAsyncValue(
    () =>
      //Event Seats ID never changes
      cacheRequest(`event-${activity.subjectId}-for-activity`, () => getEventWithFlatSeats(activity.subjectId), 999999),
    [activity.subjectId]
  );

  useErrorHandler(error);
  if (loading) {
    return <Icon name="activity" loading />;
  }
  if (mappedSeats && activity) {
    return <GrouppedSeatsBlockedActivity mappedSeats={mappedSeats} activity={activity} />;
  } else {
    return <></>;
  }
};

function filterAndGroupAndMappedSeats(
  { seats, sections }: FlatVenuePlanSection,
  targetSeatsId: string[]
): GrouppedBlockedSection[] {
  const targetSeats = compact(targetSeatsId.map((seatId) => seats[seatId]));
  const grouppedBySection = groupBy(targetSeats, 'section');
  const mappedGroups = Object.entries(grouppedBySection).map(([sectionId, sectionSeats]) => {
    const grouppedByRow = groupBy(sectionSeats, 'row');
    const section = sections[sectionId];
    return {
      name: section?.name || '',
      allSeats: section?.capacity === sectionSeats.length,
      rows: Object.entries(grouppedByRow).map(([row, rowSeats]) => {
        return {
          name: row,
          seats: getSequences(compact(rowSeats.map((seat) => seat.number))),
        };
      }),
    };
  });
  return mappedGroups;
}

const GrouppedSeatsBlockedActivity = ({
  mappedSeats,
  activity,
}: {
  mappedSeats: FlatVenuePlanSection;
  activity: SeatsBlockedActivity | SeatsUnBlockedActivity;
}): JSX.Element => {
  const sections = useMemo(
    () => filterAndGroupAndMappedSeats(mappedSeats, activity.modifications.itemIds),
    [activity.id]
  );
  const [groupOpenStatus, setGroupOpenStatus] = useState(Array(sections.length).fill(false));

  return (
    <div className="seats-blocked-activity__container">
      {activity.action === 'seats_blocked' && (
        <span>{`${t.model.contingent.blockReason}: ${activity.modifications.reason}`}</span>
      )}

      {sections.map((section, index) => {
        return (
          <div className="section__container" key={`section-${section.name}`}>
            <div className="section__name-container">
              <span>{`Section: ${section.name}`}</span>
              <div>
                {section.allSeats ? (
                  <span className="section__all-seats">{t.model.activity.blockedSeats.allSeats}</span>
                ) : (
                  <div
                    className={classnames('section__rows-toggle', {
                      'section__rows-toggle--open': groupOpenStatus[index],
                    })}
                    onClick={() => {
                      setGroupOpenStatus((groupOpenStatus) => {
                        const newOpenStatus = [...groupOpenStatus];
                        newOpenStatus[index] = !groupOpenStatus[index];
                        return newOpenStatus;
                      });
                    }}
                  />
                )}
              </div>
            </div>
            {!section.allSeats && (
              <Collapse open={groupOpenStatus[index]}>
                <KeyValueTable
                  labelWidth={100}
                  items={[
                    { key: t.model.activity.blockedSeats.row, value: t.model.activity.blockedSeats.seats },
                    ...section.rows.map((row) => ({ key: row.name, value: row.seats.join(', ') })),
                  ]}
                />
              </Collapse>
            )}
          </div>
        );
      })}
    </div>
  );
};
